// @ts-check
import { gql } from '@apollo/client/core';
import { Image, Page, View } from '@react-pdf/renderer';
// @ts-ignore ong files can't be imported.
// import stencil from './stencils/4u20.png';
import React from 'react';
import { Text, Title } from '../../../pdf-design-system';
import { ThemeProvider } from '../../../pdf-design-system/useTheme';
import PageHeader from './UrbanihofInfoSheetPageHeader';
import Product, { ProductHeader } from './UrbanihofInfoSheetProduct';
import Footer from './UrbanihofInfoSheetFooter';
import BottleImage from './UrbanihofInfoSheetBottleImage';
import garamondSemiBold from '../fonts/garamondSemiBold';
import garamond from '../fonts/garamond';
import messages from '../locale/messages';
import UserLocaleProvider from '../../../Providers/UserLocaleProvider';

export default function UrbanihofInfoSheet({ data, locale, ...rest }) {
  return (
    <UserLocaleProvider locale={locale} messages={messages}>
      <Page size="A4" style={{ paddingHorizontal: '12mm', paddingTop: '15mm' }}>
        <ThemeProvider theme={theme}>
          <Layout data={data} {...rest} />
        </ThemeProvider>
      </Page>
    </UserLocaleProvider>
  );
}

/** @param {import('./@types/UrbanihofInfoSheet').UrbanihofInfoSheetFragment} rawProduct */
UrbanihofInfoSheet.useMetaData = function useMetaData(rawProduct) {
  const product = afterQuery(rawProduct, {});
  return {
    title: `${product.producer?.name} ${product.name} Datenblatt`,
  };
};

function Layout({ data, ...rest }) {
  try {
    const product = afterQuery(data, rest);
    return (
      <ProductProvider product={product}>
        {/* <Stencil /> */}
        <View style={{ height: '100%' }}>
          <View style={{ flexShrink: 0 }}>
            <PageHeader style={{ marginBottom: '2mm' }} />
            <ProductHeader style={{}} />
          </View>
          <View style={{ flexGrow: 1, flexDirection: 'row' }}>
            <MainColumn style={{ marginTop: '10mm' }}>
              <Product style={{ flexGrow: 1, marginTop: '0mm' }} />
            </MainColumn>
            <ImageColumn style={{ width: '45mm', marginLeft: '2mm' }}>
              <BottleImage style={{}} />
            </ImageColumn>
          </View>

          <Footer style={{ marginTop: '2mm', marginBottom: '15mm' }} />
        </View>
      </ProductProvider>
    );
  } catch (error) {
    return (
      <>
        <Title>Ein Fehler ist aufgetreten.</Title>
        <Text>{error.message}</Text>
      </>
    );
  }
}

// function Stencil() {
//   return (
//     <Image
//       src={stencil}
//       style={{
//         position: 'absolute',
//         top: 0,
//         left: 0,
//         bottom: 0,
//         right: 0,
//         opacity: 0.2,
//       }}
//     />
//   );
// }

const ProductContext = React.createContext(
  /** @type {ReturnType<afterQuery>}*/ (undefined)
);

function ProductProvider({ product, children }) {
  return (
    <ProductContext.Provider value={product}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  return React.useContext(ProductContext);
}

/** Just a tiny wrapper se we can use the type from everywhere. */
export function useTheme() {
  return theme;
}

function MainColumn({ children, style, ...rest }) {
  return (
    <View style={[{ flex: 1 }, style]} {...rest}>
      {children}
    </View>
  );
}

function ImageColumn({ children, style, ...rest }) {
  return (
    <View style={[{ alignSelf: 'center' }, style]} {...rest}>
      {children}
    </View>
  );
}

/** @type {import('../../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"]} */
const theme = {
  colors: { primary: '#F36F21', text: 'cmyk(0,0,0,50)' },
  components: {
    Text: {
      base: { ...garamond },
      small: {},
      default: { fontSize: 12 },
      bold: { ...garamondSemiBold, fontSize: 12, lineHeight: 18 / 12 },
      large: { fontSize: 12, lineHeight: 18 / 12 },
    },
    Title: {
      base: { ...garamondSemiBold },
      small: {},
      default: { fontSize: 24, lineHeight: 1.2 },
      medium: { fontSize: 30, lineHeight: 1.1 },
      large: { fontSize: 35, lineHeight: 1.1 },
    },
  },
};

const parse = gql; // Helps avoid GraphQL extension errors.
UrbanihofInfoSheet.fragment = parse/* GraphQL */ `
  fragment UrbanihofInfoSheet on Product {
    productId
    name
    producer {
      name
      # Needed by apollo client
      producerId
    }
    ...UrbanihofInfoSheetPageHeader
    ...UrbanihofInfoSheetBottleImage
    ...UrbanihofInfoSheetProduct
    ...UrbanihofInfoSheetFooter
  }
  ${PageHeader.fragment}
  ${BottleImage.fragment}
  ${Product.fragment}
  ${Footer.fragment}
`;

/** @param {import('./@types/UrbanihofInfoSheet').UrbanihofInfoSheetFragment} product */
function afterQuery(product, rest) {
  if (rest?.error) throw new Error(rest?.error.message);
  if (!product) throw new Error('Product not found');
  return { ...product };
}
